"use client";

import { motion } from "framer-motion";
import React, { useCallback, useEffect, useRef } from "react";

interface MagicCardProps {
  children?: React.ReactNode;
  className?: string;
  gradientSize?: number;
  gradientColor?: string;
  gradientOpacity?: number;
  gradientFrom?: string;
  gradientTo?: string;
}

export function MagicCard({
  children,
  className,
  gradientSize = 200,
  gradientColor = "#262626",
  gradientOpacity = 0.8,
  gradientFrom = "#9E7AFF",
  gradientTo = "#FE8BBB",
}: MagicCardProps) {
  const cardRef = useRef<HTMLDivElement>(null);
  const mouseX = useRef<number>(-gradientSize);
  const mouseY = useRef<number>(-gradientSize);
  const mouseXMotion = useRef<number>(-gradientSize);
  const mouseYMotion = useRef<number>(-gradientSize);

  const handleMouseMove = useCallback(
    (e: MouseEvent) => {
      if (cardRef.current) {
        const { left, top } = cardRef.current.getBoundingClientRect();
        const clientX = e.clientX;
        const clientY = e.clientY;
        mouseX.current = clientX - left;
        mouseY.current = clientY - top;
      }
    },
    []
  );

  const handleMouseOut = useCallback(
    (e: MouseEvent) => {
      if (!e.relatedTarget) {
        document.removeEventListener("mousemove", handleMouseMove);
        mouseX.current = -gradientSize;
        mouseY.current = -gradientSize;
      }
    },
    [handleMouseMove, gradientSize]
  );

  const handleMouseEnter = useCallback(() => {
    document.addEventListener("mousemove", handleMouseMove);
    mouseX.current = -gradientSize;
    mouseY.current = -gradientSize;
  }, [handleMouseMove, gradientSize]);

  useEffect(() => {
    document.addEventListener("mousemove", handleMouseMove);
    document.addEventListener("mouseout", handleMouseOut);
    document.addEventListener("mouseenter", handleMouseEnter);

    return () => {
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseout", handleMouseOut);
      document.removeEventListener("mouseenter", handleMouseEnter);
    };
  }, [handleMouseEnter, handleMouseMove, handleMouseOut]);

  useEffect(() => {
    mouseX.current = -gradientSize;
    mouseY.current = -gradientSize;
  }, [gradientSize]);

  // Animation frame for smooth motion
  useEffect(() => {
    let animationFrameId: number;

    const render = () => {
      // Smooth transition of the gradient position
      mouseXMotion.current += (mouseX.current - mouseXMotion.current) * 0.1;
      mouseYMotion.current += (mouseY.current - mouseYMotion.current) * 0.1;

      if (cardRef.current) {
        const borderEl = cardRef.current.querySelector(
          ".magic-card-border"
        ) as HTMLElement;
        
        const spotlightEl = cardRef.current.querySelector(
          ".magic-card-spotlight"
        ) as HTMLElement;

        if (borderEl) {
          borderEl.style.background = `
            radial-gradient(
              ${gradientSize}px circle at ${mouseXMotion.current}px ${mouseYMotion.current}px,
              ${gradientFrom}, 
              ${gradientTo}, 
              transparent 100%
            )
          `;
        }

        if (spotlightEl) {
          spotlightEl.style.background = `
            radial-gradient(
              ${gradientSize}px circle at ${mouseXMotion.current}px ${mouseYMotion.current}px, 
              ${gradientColor}, 
              transparent 100%
            )
          `;
        }
      }

      animationFrameId = requestAnimationFrame(render);
    };

    render();
    return () => {
      cancelAnimationFrame(animationFrameId);
    };
  }, [gradientSize, gradientColor, gradientFrom, gradientTo]);

  return (
    <div
      ref={cardRef}
      className={`group relative rounded-xl ${className || ""}`}
      style={{ borderRadius: "inherit" }}
    >
      <div className="magic-card-border pointer-events-none absolute inset-0 rounded-[inherit] bg-white opacity-0 transition-opacity duration-300 group-hover:opacity-100"></div>
      <div className="absolute inset-px rounded-[inherit] bg-white dark:bg-gray-900"></div>
      <div 
        className="magic-card-spotlight pointer-events-none absolute inset-px rounded-[inherit] opacity-0 transition-opacity duration-300 group-hover:opacity-100"
        style={{ opacity: gradientOpacity }}
      ></div>
      <div className="relative">{children}</div>
    </div>
  );
}